// Phase 13b — Postmark inbound webhook event shape.
//
// Per 13b-2: at v1 we handle three RecordType values — Delivery,
// Bounce (Type: HardBounce or SoftBounce), and SpamComplaint. Open /
// Click / SubscriptionChange / etc. are intentionally absent from the
// discriminated union below so a future RecordType arrives as a known
// "ignored" branch rather than a parse failure.
//
// Postmark's payload reference: https://postmarkapp.com/developer/webhooks
// We model the FIELDS WE READ — Postmark sends ~30 fields per event;
// pulling the full shape into a Zod schema would be brittle to provider
// changes. The discriminated union catches schema drift at parse time
// (unknown RecordType → fall through to z.unknown branch).
//
// Per 13b-9: schemas under packages/schemas/src/notification/webhook.schema.ts
// (Convention 06 — schema per resource).
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────────────────
// Per-RecordType payload shapes (fields we actually read; Postmark sends more)
// ─────────────────────────────────────────────────────────────────────────────
// Per audit follow-up W1: MessageID is `.min(1)` (not bare `.string()`).
// Dev-fallback writes `providerMessageId: ''` on NotificationDelivery
// rows; without the min-length floor, an HMAC-signed webhook payload
// with `MessageID: ''` would parse, hit `findFirst({ providerMessageId:
// '' })`, and match a dev-fallback row. Production exposure is zero
// (no stub rows in prod; Postmark never sends ''); the floor is
// defense-in-depth.
/** Postmark `Delivery` event. Fires once the recipient ESP has accepted
 *  the message. `MessageID` is how we correlate back to the
 *  NotificationDelivery.providerMessageId set at dispatch time. */
export const PostmarkDeliveryEventSchema = z.object({
    RecordType: z.literal('Delivery'),
    MessageID: z.string().min(1),
    Recipient: z.string().optional(),
    DeliveredAt: z.string().optional(), // ISO timestamp
    Tag: z.string().optional(),
});
/** Postmark `Bounce` event. `Type` discriminates HardBounce vs
 *  SoftBounce per 13b-2 + 13b-3. Other Type values (Transient, etc.)
 *  pass through to the controller's `default` branch. */
export const PostmarkBounceEventSchema = z.object({
    RecordType: z.literal('Bounce'),
    MessageID: z.string().min(1),
    Type: z.string(), // 'HardBounce' | 'SoftBounce' | ... (Postmark documents ~20 sub-types)
    Email: z.string().optional(),
    Description: z.string().optional(),
    Details: z.string().optional(),
    BouncedAt: z.string().optional(),
    Tag: z.string().optional(),
});
/** Postmark `SpamComplaint` event. Per 13b-2 + 13-D: suppress
 *  MARKETING-category recipients only; TRANSACTIONAL still sends. */
export const PostmarkSpamComplaintEventSchema = z.object({
    RecordType: z.literal('SpamComplaint'),
    MessageID: z.string().min(1),
    Email: z.string().optional(),
    Description: z.string().optional(),
    BouncedAt: z.string().optional(),
    Tag: z.string().optional(),
});
/** Known RecordType values that have dedicated schemas above. Used by
 *  `PostmarkUnknownEventSchema` to refuse to match a known RecordType
 *  whose typed schema failed validation (e.g. `Delivery` with an empty
 *  `MessageID` per audit W1). Without this refinement, `{ RecordType:
 *  'Delivery', MessageID: '' }` would fall through to the Unknown
 *  branch instead of correctly failing the union — letting an empty
 *  MessageID through to the service and potentially matching against
 *  dev-fallback rows. */
const KNOWN_RECORD_TYPES = ['Delivery', 'Bounce', 'SpamComplaint'];
/** Fallback for unknown RecordType values (Open / Click /
 *  SubscriptionChange / etc.) — controller logs at debug + returns 200
 *  per 13b-2 ("ignored"). The schema accepts these so a partial
 *  upgrade doesn't fail the entire batch. Refuses to match known
 *  RecordTypes whose typed schemas failed (W1). */
export const PostmarkUnknownEventSchema = z
    .object({
    RecordType: z.string().refine((v) => !KNOWN_RECORD_TYPES.includes(v), {
        message: 'RecordType has a typed schema with stricter rules; falling through to Unknown is forbidden (W1)',
    }),
})
    .passthrough();
// ─────────────────────────────────────────────────────────────────────────────
// Discriminated union — main entry point for the controller
// ─────────────────────────────────────────────────────────────────────────────
export const PostmarkWebhookEventSchema = z.union([
    PostmarkDeliveryEventSchema,
    PostmarkBounceEventSchema,
    PostmarkSpamComplaintEventSchema,
    PostmarkUnknownEventSchema,
]);
// ─────────────────────────────────────────────────────────────────────────────
// Phase 13b-6 — admin healing-trigger response shape
// ─────────────────────────────────────────────────────────────────────────────
/** Response from `POST /admin/notifications/heal-stuck-deliveries`. */
export const HealStuckDeliveriesResponseSchema = z.object({
    reEnqueuedCount: z.number().int().min(0),
    deliveryIds: z.array(z.string().uuid()),
});
/** RC2 (2026-09-02) — response from
 *  `POST /admin/notifications/deliveries/:id/resend`. The delivery was flipped
 *  FAILED → PENDING and a fresh dispatch job enqueued. */
export const ResendDeliveryResponseSchema = z.object({
    deliveryId: z.string().uuid(),
    status: z.literal('PENDING'),
});
//# sourceMappingURL=webhook.schema.js.map